import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";

import router from "./routes/router";

import { clear, set } from "idb-keyval";
import {
  setFreeCategories,
  setFreeSheets,
  setFreeVaccins,
} from "./store/reducers/freeContent.slice";
import { GetAllData, GetAllDataVaccins } from "./types/api/allDatas";
import { UserLogin, UserRole } from "./types/api/user";
import api, { removeToken, setToken } from "./utils/api/axios";
import { notifyApiSuccess, notifyApiWarning } from "./utils/api/notifyApi";

import { useAppDispatch, useAppSelector } from "./store/hook";
import {
  setAuthUser,
  setHasNoNetwork,
  setNotifiedHasNetwork,
  setNotifiedHasNoNetwork,
  setNotifiedOfflineOn,
  setIsLoadingData,
  setDataLoaded,
} from "./store/reducers/auth.slice";
import { GetCategory } from "./types/api/category";
import { DecisionTree } from "./types/api/decision-tree";
import { GetSheet } from "./types/api/sheet";

import ReactGA from "react-ga4";
import CookieConsentModal from "./components/CookieConsentModal";
import {
  debugAnalytics,
  disableAnalytics,
  initializeAnalytics,
  setUserProperties,
  trackLogin,
  trackPageView,
} from "./utils/analytics";
import {
  ConsentStatus,
  hasConsentChoice,
  saveConsentChoice,
} from "./utils/cookieConsent";

declare global {
  interface Window {
    dataLayer: any[];
    debugAnalytics: (user?: any) => void;
  }
}

function App() {
  const dispatch = useAppDispatch();
  const auth = useAppSelector((state) => state.auth);
  const {
    isAuth,
    hasNoNetwork,
    user,
    notifiedHasNetwork,
    notifiedHasNoNetwork,
    notifiedOfflineOn,
  } = auth;

  const [showCookieConsent, setShowCookieConsent] = useState(
    !hasConsentChoice()
  );

  const handleAcceptCookies = () => {
    saveConsentChoice(ConsentStatus.ACCEPTED);
    setShowCookieConsent(false);
    initializeAnalytics();
  };

  const handleDeclineCookies = () => {
    saveConsentChoice(ConsentStatus.DECLINED);
    setShowCookieConsent(false);
    disableAnalytics();
  };

  useEffect(() => {
    window.debugAnalytics = (testUser?: any) => {
      const userToTest = testUser || user;
      debugAnalytics(userToTest);
    };
  }, [user]);

  const setSheets = async (sheets: GetSheet[]) => {
    await Promise.all(
      sheets.map(async (sheet) => {
        if (sheet.sheet) await set("sheet-" + sheet.sheet.id, sheet);
      })
    );
  };

  const setCategories = async (categories: GetCategory[]) => {
    await Promise.all(
      categories.map(async (category) => {
        if (category) await set("category-" + category.category.id, category);
      })
    );
  };

  const setVaccins = async (vaccins: GetAllDataVaccins[]) => {
    await Promise.all(
      vaccins.map(async (vaccin) => {
        if (vaccin) await set("vaccin-" + vaccin.id, vaccin);
      })
    );
  };

  const setDecisionTrees = async (decisionTrees: DecisionTree[]) => {
    await set("decision-trees", decisionTrees);
  };

  // init to false to avoid succes message on first load
  const fetchAllDatas = async () => {
    try {
      dispatch(setIsLoadingData(true));
      dispatch(setDataLoaded(false));
      const { data } = await api.get<GetAllData | boolean>("/all-data");
      // is case subscription is not active
      if (!data || typeof data === "boolean") {
        if (data === false) {
          clear();
        }
        dispatch(setIsLoadingData(false));
        return;
      }
      clear();
      const {
        categories,
        sheets,
        vaccins,
        decisionTrees,
        freeCategories,
        freeSheets,
        freeVaccins,
      } = data;
      if (freeCategories) dispatch(setFreeCategories(freeCategories));
      if (freeSheets) dispatch(setFreeSheets(freeSheets));
      if (freeVaccins) dispatch(setFreeVaccins(freeVaccins));

      await setDecisionTrees(decisionTrees);
      await setSheets(sheets);
      await setCategories(categories);
      await setVaccins(vaccins);
      dispatch(setIsLoadingData(false));
      dispatch(setDataLoaded(true));
      if (!notifiedOfflineOn) {
        notifyApiSuccess("Votre mode hors ligne est opérationnel");
        dispatch(setNotifiedOfflineOn(true));
      }
    } catch (_) {
      dispatch(setIsLoadingData(false));
    }
  };

  const checkUserEstablishmentConnexion = async () => {
    try {
      const { data: ipData } = await api.post<UserLogin | boolean | undefined>(
        "/auth/ip/login"
      );
      if (ipData && typeof ipData !== "boolean") {
        //notifyApiSuccess("Vous êtes connecté via votre IP");
        dispatch(setAuthUser(ipData));
        setToken(ipData.token.token);
        trackLogin(ipData, "ip");
        return;
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        const token =
          urlParams.get("token") || localStorage.getItem("establishmentToken");
        const { data: tokenData } = await api.post<
          UserLogin | boolean | undefined
        >("/auth/token/login/", null, {
          params: {
            token,
          },
        });
        if (tokenData && typeof tokenData !== "boolean") {
          //  notifyApiSuccess("Vous êtes connecté via votre token");
          dispatch(setAuthUser(tokenData));
          setToken(tokenData.token.token);
          localStorage.setItem("establishmentToken", token || "");
          trackLogin(tokenData, "token");
          return;
        } else if (
          user?.role === UserRole.ESTABLISHMENT &&
          ipData !== true &&
          tokenData !== true
        ) {
          try {
            await api.post("/auth/logout");
          } catch (_) {
            console.error("Logout failed");
          }
          removeToken();
        }
      }
    } catch (_) {}
  };

  // Track anonymous users on first load
  useEffect(() => {
    if (!isAuth && initializeAnalytics()) {
      const trackAnonymous = async () => {
        await trackPageView(window.location.pathname + window.location.search);
      };
      trackAnonymous();

      // Configure GA4 for anonymous users
      ReactGA.gtag("config", process.env.REACT_APP_GA4_TRACKING_ID!, {
        send_page_view: false,
      });
    }
  }, [isAuth]);

  // Track authenticated users after login
  useEffect(() => {
    if (isAuth && user && initializeAnalytics()) {
      const trackAuthenticated = async () => {
        await trackPageView(
          window.location.pathname + window.location.search,
          document.title,
          user
        );
      };
      trackAuthenticated();

      // Set user properties in GA4
      setUserProperties(user);

      // Configure GA4 for authenticated users
      ReactGA.gtag("config", process.env.REACT_APP_GA4_TRACKING_ID!, {
        user_id: user.id.toString(),
        send_page_view: false,
      });
    }
  }, [isAuth, user]);

  useEffect(() => {
    if (!hasNoNetwork) {
      checkUserEstablishmentConnexion();
      fetchAllDatas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNoNetwork, isAuth]);

  useEffect(() => {
    if (hasNoNetwork) {
      if (!notifiedHasNoNetwork) {
        dispatch(setNotifiedHasNoNetwork(true));
        notifyApiWarning("Vous n'avez pas de connexion internet");
      }
      if (notifiedHasNetwork) {
        dispatch(setNotifiedHasNetwork(false));
      }
      const intervalId = setInterval(async () => {
        try {
          await api.get("/network-test");
          if (!notifiedHasNetwork) {
            dispatch(setNotifiedHasNetwork(true));
            notifyApiSuccess("Vous êtes connecté à internet");
          }
          dispatch(setNotifiedHasNoNetwork(false));
        } catch (_) {
          dispatch(setHasNoNetwork(true));
        }
      }, 5000);
      return () => clearInterval(intervalId);
    }
  }, [hasNoNetwork, dispatch, notifiedHasNetwork, notifiedHasNoNetwork]);

  return (
    <>
      <RouterProvider router={router} />
      {showCookieConsent && (
        <CookieConsentModal
          open={showCookieConsent}
          onAccept={handleAcceptCookies}
          onDecline={handleDeclineCookies}
        />
      )}
    </>
  );
}

export default App;
